import React, { useState } from "react";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Kicker, WfButton } from "@/components/site/primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EMAIL_LIST } from "@/content/getInvolved";
import { cn } from "@/lib/utils";

/**
 * SubscribeSection — email list sign-up (MOCKED until the email provider is
 * connected). Reused on /get-involved and /contact.
 */
export const SubscribeSection = ({ id = "email-list", className = "bg-[var(--wf-cream)]", testidPrefix = "subscribe" }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", zip: "" });
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.firstName.trim()) next.firstName = "Please enter your first name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = "Please enter a valid email address.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setDone(true);
    toast.success("You’re on the list!", { description: "Watch your inbox for news from the farm. (Demo — email provider not yet connected.)" });
  };

  const field = "min-h-11 rounded-lg border-[var(--wf-border)] bg-white focus-visible:ring-[var(--wf-maroon)]";

  return (
    <section id={id} aria-labelledby={`${id}-heading`} className={cn("wf-section scroll-mt-20", className)}>
      <div className="wf-container">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
          <Reveal>
            <Kicker>{EMAIL_LIST.eyebrow}</Kicker>
            <h2 id={`${id}-heading`} className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">{EMAIL_LIST.title}</h2>
            <p className="mt-4 text-base leading-7 text-[var(--wf-soil)]">{EMAIL_LIST.body}</p>
            <ul className="mt-6 space-y-2 text-sm text-[var(--wf-ink)]">
              {["Monthly newsletter from the farm", "CSA and farmers’ market announcements", "Invitations to Tour de Farm and other events"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-green-text)]" aria-hidden="true" />{t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            {done ? (
              <div role="status" data-testid={`${testidPrefix}-success`} className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-8 shadow-[var(--wf-shadow-sm)]">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--wf-farm-green)] text-white"><Mail className="h-6 w-6" aria-hidden="true" /></span>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-[var(--wf-ink)]">Thanks, {form.firstName}!</h3>
                <p className="mt-2 text-base leading-7 text-[var(--wf-soil)]">We’ve added <strong>{form.email}</strong> to the World Farmers list. You can unsubscribe at any time.</p>
                <p className="mt-4 text-xs text-muted-foreground">Demo mode: the email provider is not connected yet.</p>
                <button type="button" onClick={() => { setDone(false); setForm({ firstName: "", lastName: "", email: "", zip: "" }); }} data-testid={`${testidPrefix}-reset`} className="mt-6 text-sm font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">Sign up another address</button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid={`${testidPrefix}-form`} className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-6 shadow-[var(--wf-shadow-sm)] sm:p-8">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`${id}-first`} className="text-sm font-semibold text-[var(--wf-ink)]">First name</Label>
                    <Input id={`${id}-first`} autoComplete="given-name" value={form.firstName} onChange={set("firstName")} aria-invalid={!!errors.firstName} data-testid={`${testidPrefix}-first-name-input`} className={field} />
                    {errors.firstName && <p role="alert" data-testid={`${testidPrefix}-first-name-error`} className="text-sm text-[var(--wf-maroon)]">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`${id}-last`} className="text-sm font-semibold text-[var(--wf-ink)]">Last name <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Input id={`${id}-last`} autoComplete="family-name" value={form.lastName} onChange={set("lastName")} data-testid={`${testidPrefix}-last-name-input`} className={field} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`${id}-email`} className="text-sm font-semibold text-[var(--wf-ink)]">Email address</Label>
                    <Input id={`${id}-email`} type="email" inputMode="email" autoComplete="email" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} data-testid={`${testidPrefix}-email-input`} className={field} />
                    {errors.email && <p role="alert" data-testid={`${testidPrefix}-email-error`} className="text-sm text-[var(--wf-maroon)]">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`${id}-zip`} className="text-sm font-semibold text-[var(--wf-ink)]">ZIP code <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Input id={`${id}-zip`} inputMode="numeric" autoComplete="postal-code" value={form.zip} onChange={set("zip")} data-testid={`${testidPrefix}-zip-input`} className={field} />
                  </div>
                </div>
                <WfButton type="submit" withArrow className="mt-6 w-full sm:w-auto sm:px-8" data-testid={`${testidPrefix}-submit-button`}>Join the list</WfButton>
                <p className="mt-3 text-xs text-muted-foreground">We’ll never share your email. Unsubscribe anytime. Demo form — provider connection coming soon.</p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
